import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Loader2 } from 'lucide-react';
import { useEntityOptions } from '../../hooks/useEntitySelection';

interface EntitySelectorProps {
  entityType: string;
  value: string;
  onChange: (entityId: string, displayName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  workspaceId?: string; // When provided, only shows entities tracked in this workspace
}

const EntitySelector: React.FC<EntitySelectorProps> = ({
  entityType,
  value,
  onChange,
  placeholder = "Select entity...",
  disabled = false,
  workspaceId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDisplay, setSelectedDisplay] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: options = [], isLoading } = useEntityOptions(entityType, searchTerm, workspaceId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected display when value changes
  useEffect(() => {
    if (value && options.length > 0) {
      const selected = options.find((option: any) => option.id === value);
      if (selected) {
        setSelectedDisplay(selected.display_name);
      }
    } else if (!value) {
      setSelectedDisplay('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Remove options dependency to prevent infinite loop

  const handleSelect = (option: any) => {
    onChange(option.id, option.display_name);
    setSelectedDisplay(option.display_name);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      // Focus the search input when dropdown opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const filteredOptions = options.filter((option: any) =>
    option.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main Input */}
      <div
        onClick={handleInputClick}
        className={`
          flex items-center justify-between px-3 py-2 border border-border-light dark:border-border-dark 
          rounded-md cursor-pointer bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary-400 dark:hover:border-primary-500'}
          ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}
        `}
      >
        <span className={selectedDisplay ? 'text-text-primary dark:text-text-dark-primary' : 'text-text-tertiary dark:text-text-dark-tertiary'}>
          {selectedDisplay || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 card p-0 max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border-light dark:border-border-dark">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search ${entityType}s...`}
                className="input w-full pl-9"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading...</span>
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option: any) => (
                <div
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  className="px-3 py-2 cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark last:border-b-0 text-left"
                >
                  <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary text-left">
                    {option.display_name}
                  </div>
                  {option.additional_info && (
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 text-left">
                      {option.additional_info}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
                {searchTerm ? `No ${entityType}s found matching "${searchTerm}"` : `No ${entityType}s available`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntitySelector;

